import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { HeaderWash, V2, V2Icon } from '../components/v2'
import { usesAppLook } from '../lib/variant'

const checklistItems = [
  { text: 'Analyzing your kidney profile', emoji: '🏥' },
  { text: 'Calculating your nutrient limits', emoji: '📊' },
  { text: 'Personalizing meal recommendations', emoji: '🍽️' },
  { text: 'Calibrating AI for your needs', emoji: '🤖' },
]

export function AnalysisStep() {
  const { goNext } = useOnboardingStore()
  const [progress, setProgress] = useState(0)
  const [completedItems, setCompletedItems] = useState<number[]>([])
  const v2 = usesAppLook()

  useEffect(() => {
    const duration = 8000
    const startTime = Date.now()

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min((elapsed / duration) * 100, 100)
      setProgress(newProgress)

      // Complete items at specific progress points
      if (newProgress >= 25 && !completedItems.includes(0)) setCompletedItems(prev => [...prev, 0])
      if (newProgress >= 50 && !completedItems.includes(1)) setCompletedItems(prev => [...prev, 1])
      if (newProgress >= 75 && !completedItems.includes(2)) setCompletedItems(prev => [...prev, 2])
      if (newProgress >= 95 && !completedItems.includes(3)) setCompletedItems(prev => [...prev, 3])

      if (newProgress >= 100) {
        clearInterval(interval)
        setTimeout(() => goNext(), 1000)
      }
    }, 50)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className={v2 ? `min-h-dvh flex flex-col relative ${V2.page}` : 'min-h-dvh flex flex-col bg-white'}>
      {v2 && <HeaderWash />}
      <div className={v2 ? 'flex-1 flex flex-col items-center justify-center px-6 py-12 relative z-10' : 'flex-1 flex flex-col items-center justify-center px-6 py-12'}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            {v2 ? <V2Icon name="mascot-clipboard" size={96} className="block mx-auto mb-4" /> : <div className="text-6xl mb-4">🔬</div>}
            <h1 className={v2 ? `text-2xl font-bold ${V2.text} mb-2` : 'text-2xl font-bold text-gray-900 mb-2'}>
              Building your personal plan
            </h1>
            <p className={v2 ? V2.textSecondary : 'text-gray-500'}>
              Hang tight - we're customizing everything for you
            </p>
          </div>

          {/* Progress bar */}
          <div className="w-full h-3 bg-gray-100 rounded-full mb-8 overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          {/* Checklist */}
          <div className="space-y-4">
            {checklistItems.map((item, i) => {
              const isVisible = progress >= i * 25
              const isComplete = completedItems.includes(i)

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-3"
                >
                  {isComplete ? (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.div>
                  ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center shrink-0">
                      <div className="w-3 h-3 rounded-full bg-gray-300 animate-pulse" />
                    </div>
                  )}
                  <span className={`font-medium ${isComplete ? 'text-gray-900' : 'text-gray-400'}`}>
                    {item.text}
                  </span>
                </motion.div>
              )
            })}
          </div>
        </motion.div>
      </div>
    </div>
  )
}
